import React, { useMemo, useRef, useState } from "react";
import { IconButton } from '@mui/material';
import {Print} from '@mui/icons-material';
import { useAlert } from "components/UI/alertSakeBar/alertProvider";
import Loading from "components/Loading";
import { useReactToPrint } from "react-to-print";
import styled from "styled-components";
import { handleTypeText } from "./helper";
import {Mic} from '@mui/icons-material';
import { Tooltip } from 'antd';
import printSvg from "../../utils/assets/images/print.svg";
import { useWorksheetView } from "store/worksheetView/selector";
import { getBookDisplayName } from "utils/lib/ebookHelper";
import { useAllBook } from "store/books/selectors";
import { useResources, useVideos } from "store/resources/selectors";
import { useDeviceDetection ,useIsTab } from 'containers/chat/hooks';
import { useMobileDetect } from "hooks/useMobileDetect";
import FillInTheBlanksPrint from "modules/print/FillInTheBlanksPrint";
import usePdfFromRef from 'components/hooks/usePdfFromRef';
import { handleListQuestionOfWorksheet } from "store/worksheet/thunks";
import { useDispatch } from "react-redux";

// Styled Components for Printing
const PrintStyles = styled.div`
  @media print {
    body,
    html {
      width: 100%;
      height: 100vh;
      overflow: visible !important;
    }

    .printable {
      display: block;
      font-family: Arial, sans-serif;
    }

    .question-row {
      margin-bottom: 40px;
      padding-bottom: 20px;
      border-bottom: 1px solid #ddd;
      page-break-inside: avoid;
    }

    .question-header {
      font-weight: bold;
      margin-bottom: 15px;
      font-size: 16px;
    }

    .question-options {
      display: flex;
      flex-wrap: wrap;
      gap: 20px;
      margin-left: 20px;
      font-size: 14px;
      color: #333;
    }

    .option {
      width: calc(50% - 10px);
      margin-bottom: 10px;
      box-sizing: border-box;
    }

    .question-answer {
      margin-top: 10px;
      font-size: 14px;
      font-weight: bold;
      color: #000;
    }

    .question-row:nth-child(n + 2) {
      page-break-before: always;
    }
  }
`;

const QuestionContainer = styled.div`
  font-size: 17px;
  display: flex;
  align-items: center;
`;

const PrintContainer = styled.div`
  padding: 40px;
`;
const PageDataInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 50px;
  margin-top: -20px;
  span {
    font-size: 14px;
    font-weight: 800;
  }
`;

const PrintStylesQuestion = styled.ul`
  li {
    margin-left: 40px;
    list-style-type: circle;
    font-size: 17px;
  }
`;

const ImageContainer = styled.p`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  height: auto;
  margin-top: 20px;
  margin-bottom: 10;
  flex-wrap: wrap;
  margin-left: 22px;
  gap: 10px;
`;

const QuestionType = styled.p`
  font-weight: 900;
  margin-bottom: 0px;
  margin-left: 18px;
  margin-top: 20px;
  margin-bottom: -20px;
`;

// Print Content Component
const PrintComponentQuestions = React.forwardRef(
  ({ questions, InfoWorkSheet, numberMasked }, ref) => {
    return (
      <PrintStyles>
        <PrintContainer ref={ref} className="print-container printable">
          <PageDataInfo>
            <span>{InfoWorkSheet?.title}</span>
            <span>{InfoWorkSheet?.GetBookName}</span>
            <span>Page: { isNaN(InfoWorkSheet?.bookId) ? numberMasked : InfoWorkSheet?.bookId}</span>
          </PageDataInfo>
          <div style={{ width: "100%" }}>
            {questions?.map((question, index) => (
              <div key={index} className="question-row question-do-not-break">
                <QuestionType>
                  {handleTypeText(question?.type)}
                  {question?.tense ? ` - (${question.tense})` : ""}
                </QuestionType>

                <ImageContainer>
                  {Array.isArray(question?.image) ? (
                    question.image.map((img, i) => (
                      <img
                        key={i}
                        src={img}
                        alt={`Question  ${i + 1}`}
                        style={{ maxHeight: "120px", borderRadius: "8px" }}
                      />
                    ))
                  ) : question?.image ? (
                    <img
                      src={question.image}
                      alt="Question "
                      style={{ maxHeight: "120px", borderRadius: "8px" }}
                    />
                  ) : null}
                </ImageContainer>
                <QuestionContainer className="question-header">
                  <strong>{index + 1}.</strong>{" "}
                  {question?.type === "Select_What_You_Hear" ? (
                    <>
                      <Mic
                        style={{ verticalAlign: "middle", marginRight: "5px" }}
                      />
                      (<span dangerouslySetInnerHTML={{ __html: question.question || '' }} />)
                    </>
                  ) : (
                    <>
                      {question.type === "Fill_IN_THE_Blank" ? (
                        <FillInTheBlanksPrint
                          highlightWords={question?.highlightWords}
                          question={question?.question}
                        />
                      ) : (
                        <span dangerouslySetInnerHTML={{ __html: question.question || '' }} />
                      )}
                    </>
                  )}
                </QuestionContainer>
                {question.type === "Fill_IN_THE_Blank" ? null : (
                  <>
                    {question.options && question.options.length > 0 && (
                      <div className="question-options">
                        <PrintStylesQuestion>
                          {question.options.map((option, i) => (
                            <li key={i}>{option}</li>
                          ))}
                        </PrintStylesQuestion>
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        </PrintContainer>
      </PrintStyles>
    );
  }
);

// Main Print Component
export const WorkSheetPrint = ({ questions = [], chapter, from, worksheetId = null }) => {
  const componentRef = useRef();
  const dispatch = useDispatch();
  const [printLoader, setPrintLoader] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const showAlert = useAlert();
  const [printQuestions, setPrintQuestions] = useState(questions);
  const InfoWorkSheet = useWorksheetView();
  const bookAllList = useAllBook();
  const resources = useResources();
  const videos = useVideos();
  const book_name = useMemo(() => {
    return getBookDisplayName(bookAllList, chapter, false, videos, resources);
  }, [bookAllList, chapter, videos, resources]);
  const isTablate = useIsTab();
  const isMobile = useMobileDetect();
  const { openPdfInNewTabFromRef } = usePdfFromRef();
  const waitForNextPaint = () =>
    new Promise((resolve) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(resolve);
      });
    });
  const handlePrint = useReactToPrint({
    contentRef: componentRef,
    documentTitle: "Questions List",

    onBeforeGetContent: async () => {
      setPrintLoader(true);
      setIsPrinting(true);

      // Inject @page print style dynamically
      const style = document.createElement("style");
      style.id = "print-page-style";
      style.innerHTML = `
        @page {
          size: letter;
          margin-top:10mm;
          margin-bottom:20mm;
          margin-left:10mm;
          margin-right:10mm;
        }
      `;
      document.head.appendChild(style);
    },

    onBeforePrint: async () => {
      setPrintLoader(false);
      setIsPrinting(false);
    },

    onAfterPrint: async () => {
      setPrintLoader(false);
      setIsPrinting(false);
      const style = document.getElementById("print-page-style");
      if (style) document.head.removeChild(style);
    },

    onPrintError: async () => {
      setPrintLoader(false);
      setIsPrinting(false);
      const style = document.getElementById("print-page-style");
      if (style) document.head.removeChild(style);
    },
  });

  const onPrint = () => {
    if (printQuestions?.length === 0) return showAlert("No questions to print!", "error");
    handlePrint();
  };

  const openPdfInNewTab = async () => {
    await openPdfInNewTabFromRef({
      componentRef,
      onLoadingChange: setPrintLoader,
      onError: (message) => showAlert(message, 'error'),
      fileName: `${InfoWorkSheet?.assignmentInfo?.displayName || 'document'}.pdf`
    });
  };

  const fetchData = async () => {
    setPrintLoader(true);
    const response = await dispatch(handleListQuestionOfWorksheet(worksheetId));
    const formattedQuestions = [];
    if (Array.isArray(response)) {
      response.forEach((element) => {
        if (!element?.id) return;
        element.question.id = element?.question_id;
        const questionData = {
          ...element?.question,
          image: element?.image,
          answer_audio: element?.answer_audio,
          question_audio: element?.question_audio,
        };
        formattedQuestions.push(questionData);
      });
    }
    setPrintQuestions(formattedQuestions);
    setPrintLoader(false);
    return formattedQuestions;
  }

  const handlePrintAssignment = async (e) => {
    const latestPrintQuestions = await fetchData();
    if (latestPrintQuestions?.length === 0) return showAlert("No questions to print!", "error");
    setPrintQuestions(latestPrintQuestions);
    await waitForNextPaint();
    if (isMobile || isTablate) {
      await openPdfInNewTab();
    } else {
      await handlePrint();
    }
  }
  return (
    <>
    <div className="w-fit-content" style={{ cursor: "pointer" }}>
      {
        from === "ebook" ? (
          <>{
            printLoader ? (
              <Loading size={20} />
            ) : (
                <img onClick={handlePrintAssignment}  style={{height: "17px", width: "17px"}} src={printSvg} alt="" title="" /> 
            )
          }</>
        ) : (
            <IconButton
              style={{ padding: 0, color: "white" }}
              aria-label="print"
              onClick={() => {
                if (isMobile || isTablate) {
                  openPdfInNewTab();
                  return;
                } else {
                  onPrint();
                }
              }}
              disabled={isPrinting}
            >
              {printLoader ? (
                <Loading size={20} />
              ) : (<>
                <Print style={{ color: "black" }} />
              </>)}
            </IconButton>
        )
      }
      

      {/* Hidden print area */}
      <div style={{ display: "none" }} className="printable">
        <PrintComponentQuestions
          ref={componentRef}
          questions={printQuestions}
          InfoWorkSheet={InfoWorkSheet}
          book_name={book_name}
          numberMasked={chapter}
        />
      </div>
    </div>
    </>
  );
};
